/**
 * The auth API surface. Mirrors HexagieApp
 * `src/shared/services/authService.ts`.
 *
 * All three session calls return the same envelope
 * (ConsumerAuthSessionResponse) and the caller branches only on `nextStep`.
 */

import CLIENT from "../../lib/api/client";
import ENDPOINTS from "../../lib/api/endpoints";
import { clearTokens, getRefreshToken, setTokens } from "../../lib/api/tokenStore";
import { requestTokenRefresh } from "../../lib/api/refreshClient";

const AUTH_SERVICES = {
  /** Channel 1. Returns VERIFY_CHANNEL_1 or VERIFY_BOTH_CHANNELS. */
  sessionStart: (payload) => CLIENT.post(ENDPOINTS.AUTH.sessionStart, payload),

  /** An OTP leg. Returns SIGNUP_DETAILS, VERIFY_CHANNEL_2, or COMPLETE. */
  sessionVerify: (payload) => CLIENT.post(ENDPOINTS.AUTH.sessionVerify, payload),

  /** Channel 2 + name. Returns COMPLETE, or VERIFY_CHANNEL_2. */
  sessionDetails: (payload) =>
    CLIENT.post(ENDPOINTS.AUTH.sessionDetails, payload),

  /** The standalone OTP pair, used outside the session flow. */
  otpRequest: (payload) =>
    CLIENT.post(ENDPOINTS.AUTH.otpRequest, { role: "CUSTOMER", ...payload }),

  otpVerify: (payload) => CLIENT.post(ENDPOINTS.AUTH.otpVerify, payload),

  refresh: (refreshToken) => requestTokenRefresh(refreshToken),

  /** Note: returns camelCase OR snake_case keys — see consumerMappers. */
  fetchMe: () => CLIENT.get(ENDPOINTS.AUTH.userProfile),

  updateMe: (payload) => CLIENT.patch(ENDPOINTS.AUTH.updateUserProfile, payload),

  fetchSelf: () => CLIENT.get(ENDPOINTS.CONSUMER.self),

  /**
   * A FULL REPLACE, not a patch. Always merge over the last full read before
   * posting, or you will blank the fields you omitted.
   */
  saveConsumer: (payload) => CLIENT.post(ENDPOINTS.CONSUMER.save, payload),

  getUploadRequest: (fileName) =>
    CLIENT.get(ENDPOINTS.FILES.uploadRequest(fileName)),

  /**
   * Presigned PUT straight from the browser to the bucket. Set no header
   * other than Content-Type — extra headers break the signature. Needs
   * bucket CORS, which the mobile app never did (see D4).
   *
   * @param {string} uploadUrl
   * @param {string} uploadMethod
   * @param {File|Blob} file
   */
  uploadFile: async (uploadUrl, uploadMethod, file) => {
    const response = await fetch(uploadUrl, {
      method: (uploadMethod || "PUT").toUpperCase(),
      headers: { "Content-Type": file.type },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`File upload failed with status ${response.status}`);
    }
  },

  logout: async () => {
    try {
      await CLIENT.delete(ENDPOINTS.AUTH.logout);
    } finally {
      // Local state is cleared even if the server call fails, otherwise a
      // network blip leaves the user apparently signed in.
      clearTokens();
    }
  },
};

/** Adopt the tokens from a COMPLETE session response. */
export const acceptTokens = (tokens) => {
  if (!tokens?.authToken) return false;

  setTokens({
    authToken: tokens.authToken,
    refreshToken: tokens.refreshToken,
  });

  return true;
};

/** True when there is a refresh token to try on a cold start. */
export const canRestoreSession = () => Boolean(getRefreshToken());

export default AUTH_SERVICES;
